/**
 * templateVersion.js — spot saved projects built from an older built-in template.
 *
 * A saved project carries its own copy of the template's processes, so fixing a
 * built-in template does nothing for projects already made from it. To tell
 * those apart, a project created from a template is stamped with
 * `template_ref: { file, source, fingerprint }`, where the fingerprint hashes
 * the parts of the template that drive results. When the shipped template's
 * fingerprint no longer matches, the project was built from an older version.
 *
 * Projects saved before the stamp existed have no `template_ref`. The one change
 * recognisable without it is the 2026-09-22 cell-template fix, which removed a
 * 5x double count and added the `design_parameters` block: a cell-template
 * project with no design block predates it.
 *
 * Nothing here writes to a project. Callers show a notice and let the user
 * re-create the project; the saved copy is left as it is.
 */

import { findBuiltinTemplate } from './templateCatalog';

// The template fields that change results. Metadata (names, dates, the baked
// per-FU totals) is left out so editing a description does not flag projects.
const FINGERPRINT_FIELDS = ['functional_unit', 'design_parameters', 'coating_parameters', 'processes'];

// JSON with sorted object keys, so the hash does not depend on key order.
function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort()
      .filter((k) => value[k] !== undefined)
      .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

// 32-bit FNV-1a, hex. Detects change; not a security hash.
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/** Hash of the result-driving parts of a template. */
export function templateFingerprint(template) {
  const picked = {};
  FINGERPRINT_FIELDS.forEach((k) => { if (template?.[k] !== undefined) picked[k] = template[k]; });
  return fnv1a(stableStringify(picked));
}

/**
 * The stamp for a project created from `template`. `file` is the built-in file
 * name it was loaded under, or null for uploaded / AI-generated templates.
 * A template loaded from a customized copy saved in this browser (`_source:
 * 'user'`) is recorded as such, so it can be compared with the shipped one.
 */
export function stampTemplateRef(template, file) {
  if (!file) return { source: 'custom' };
  return {
    file,
    source: template?._source === 'user' ? 'user' : 'builtin',
    fingerprint: templateFingerprint(template),
  };
}

// Built-in templates that gained the design block in the 2026-09-22 fix.
const DESIGN_FIX_TEMPLATES = new Set([
  'nmc111_cell_manufacturing.json',
  'nmc622_cell_manufacturing.json',
  'nmc811_cell_manufacturing.json',
  'lfp_cell_manufacturing.json',
]);

export const DESIGN_FIX_DATE = '2026-09-22';

/**
 * Synchronous check for projects saved before stamping existed.
 * Returns the catalog entry when the project predates the cell-template fix.
 */
export function detectPreStampOutdated(project) {
  if (!project || project.template_ref) return null;
  const entry = findBuiltinTemplate(project.template_id);
  if (!entry || !DESIGN_FIX_TEMPLATES.has(entry.template)) return null;
  if (project.design_parameters || project.coating_parameters) return null;
  return entry;
}

/**
 * Is `project` built from something other than the current shipped template?
 *
 * @param {object} project
 * @param {(file: string) => Promise<object>} loadBuiltinTemplate  loads the shipped
 *   template, bypassing any customized copy saved in this browser
 * @returns {Promise<null | {
 *   entry: object,                       // catalog entry of the template
 *   reason: 'pre-fix'|'changed'|'custom-copy',
 *   key: string,                         // identifies this notice, for dismissal
 * }>}
 */
export async function checkTemplateStatus(project, loadBuiltinTemplate) {
  if (!project) return null;

  const preFix = detectPreStampOutdated(project);
  if (preFix) return { entry: preFix, reason: 'pre-fix', key: `pre-fix-${DESIGN_FIX_DATE}` };

  const ref = project.template_ref;
  if (!ref?.file || !ref.fingerprint) return null;
  const entry = findBuiltinTemplate(ref.file);
  if (!entry) return null;

  let current;
  try { current = await loadBuiltinTemplate(entry.template); } catch (_) { return null; }
  if (!current?.processes) return null;

  const fingerprint = templateFingerprint(current);
  if (fingerprint === ref.fingerprint) return null;
  return { entry, reason: ref.source === 'user' ? 'custom-copy' : 'changed', key: fingerprint };
}
